import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  ReactNode,
} from "react";
import getTheme from "../theme";
import { Theme } from "@mui/material/styles";

const useMode = () => {
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const savedMode = localStorage.getItem("themeMode");
    return savedMode ? (savedMode as "light" | "dark") : "dark";
  });

  const toggleMode = () => {
    setMode((prevMode) => {
      const newMode = prevMode === "dark" ? "light" : "dark";
      localStorage.setItem("themeMode", newMode);
      return newMode;
    });
  };

  const theme: Theme = useMemo(() => getTheme(mode), [mode]);

  // Add useEffect to apply the theme mode immediately when changed
  useEffect(() => {
    const rootElement = document.documentElement;
    rootElement.setAttribute("data-theme", mode);
  }, [mode]);

  return { theme, mode, toggleMode };
};

/* Shared so the header toggle drives the same state App feeds to ThemeProvider.
   Calling useMode() twice gives two independent useState pairs, which is why a
   toggle outside App silently did nothing. */
const ModeContext = createContext<ReturnType<typeof useMode> | null>(null);

export const ModeProvider = ({ children }: { children: ReactNode }) => {
  const value = useMode();
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};

export const useModeContext = () => {
  const value = useContext(ModeContext);
  if (!value) throw new Error("useModeContext must be used within ModeProvider");
  return value;
};

export default useMode;
